import type { AiDuplicateSignalsDto, AiDuplicateVerdictDto } from './wikiApi';

/** Цвет чипа в antd 6 (пресеты Tag). */
export type DuplicateChipTone = 'blue' | 'purple' | 'cyan' | 'geekblue' | 'default';

export interface DuplicateChip {
  key: string;
  label: string;
  tone: DuplicateChipTone;
}

const pct = (x: number): string => `${Math.round(x * 100)} %`;

/**
 * Чипы сигналов пары в «Дубли статей»: почему помощник считает статьи похожими.
 * Порядок — от самого веского: файл, текст, заголовок, смысл, категория.
 */
export function duplicateSignalChips(signals: AiDuplicateSignalsDto | undefined): DuplicateChip[] {
  const s = signals ?? {};
  const chips: DuplicateChip[] = [];
  for (const name of s.sameFile ?? []) chips.push({ key: `file:${name}`, label: `Один и тот же файл: ${name}`, tone: 'blue' });
  if (typeof s.text === 'number') chips.push({ key: 'text', label: `Похожий текст ${pct(s.text)}`, tone: 'purple' });
  if (typeof s.title === 'number') chips.push({ key: 'title', label: `Похожие заголовки ${pct(s.title)}`, tone: 'cyan' });
  if (typeof s.semantic === 'number') chips.push({ key: 'semantic', label: `По смыслу ${pct(s.semantic)}`, tone: 'geekblue' });
  if (s.sameCategory) chips.push({ key: 'category', label: 'Одна категория', tone: 'default' });
  return chips;
}

/** Чип вердикта ИИ; null — ИИ пару не проверял. */
export function duplicateVerdictChip(verdict: AiDuplicateVerdictDto | null | undefined): { label: string; color: 'error' | 'warning' | 'success' } | null {
  if (verdict === 'same') return { label: 'AI: дубль', color: 'error' };
  if (verdict === 'overlap') return { label: 'AI: частично повторяет', color: 'warning' };
  if (verdict === 'different') return { label: 'AI: разные', color: 'success' };
  return null;
}

/** Статус статьи словами — только для неопубликованных (опубликованные не помечаем). */
export function duplicateStatusLabel(status: string): string | null {
  if (status === 'DRAFT') return 'Черновик';
  if (status === 'REVIEW') return 'На проверке';
  return null;
}

/** Подпись над списком пар: чем искали и сколько пар проверил ИИ. */
export function duplicatesCaption(res: { aiUsed: boolean; aiChecked?: number }, pairsCount: number, aiTop = 15): string {
  const base = 'Ищем по заголовкам, тексту, общим файлам и смыслу';
  if (!res.aiUsed) return `${base}; ИИ пары не проверял`;
  const checked = res.aiChecked ?? Math.min(pairsCount, aiTop);
  const wanted = Math.min(pairsCount, aiTop);
  return checked >= wanted ? `${base}; ИИ проверил топ-${aiTop}` : `${base}; ИИ успел проверить ${checked} из ${wanted} верхних пар`;
}
